import { ActionMenu, ActionList, Button, Text, Stack, TextInput } from "@primer/react";
import { ChevronLeftIcon, SearchIcon, XCircleFillIcon } from '@primer/octicons-react';
import { FilterOption } from "../../../types/common/filters";
import React, { useState, useEffect, useRef } from "react";
import { FilterGroup } from "../../scoring/components/GroupedSelectFormModal";

interface SelectFormModalProps {
    selectFilterOpened: boolean;
    setSelectFilterOpened: (value: boolean) => void;
    label: string;
    items: FilterOption[];
    selectedValues: Set<string>;
    onSelect: (value: string) => void;
    onDismiss: () => void;
    isLoading?: boolean;
    hasMore?: boolean;
    isLoadingMore?: boolean;
    onLoadMore?: () => void;
    mode?: 'immediate' | 'confirm';
    groups?: FilterGroup[];
    isGrouped?: boolean;
    isShowBack?: boolean;
    searchPlaceholder?: string;
    onApply?: (selectedValues: Set<string>) => void;
    onClear?: () => void;
    onSearch?: (query: string) => void;
}

export const SelectFormModal: React.FC<SelectFormModalProps> = ({
    selectFilterOpened,
    setSelectFilterOpened,
    label,
    items,
    selectedValues,
    onSelect,
    onDismiss,
    isLoading = false,
    hasMore = false,
    isLoadingMore = false,
    onLoadMore,
    mode = 'immediate',
    groups,
    isGrouped = false,
    isShowBack = true,
    searchPlaceholder = "Поиск",
    onApply,
    onClear,
    onSearch
}) => {
    const [localSelectedValues, setLocalSelectedValues] = useState<Set<string>>(new Set());
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState('');

    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const onSearchRef = useRef(onSearch);
    const lastOpenStateRef = useRef(selectFilterOpened);

    useEffect(() => {
        onSearchRef.current = onSearch;
    }, [onSearch]);

    useEffect(() => {
        if (mode === 'confirm' && selectFilterOpened && !lastOpenStateRef.current) {
            setLocalSelectedValues(new Set(selectedValues));
        }

        if (!selectFilterOpened && lastOpenStateRef.current) {
            setSearchQuery('');
        }

        lastOpenStateRef.current = selectFilterOpened;
    }, [selectFilterOpened, selectedValues, mode]);

    const handleSearchChange = (value: string) => {
        setSearchQuery(value);

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (value.trim().length === 0) {
            if (onSearchRef.current) {
                onSearchRef.current('');
            }
            return;
        }

        if (onSearchRef.current) {
            searchTimeoutRef.current = setTimeout(() => {
                onSearchRef.current?.(value);
            }, 300);
        }
    };

    useEffect(() => {
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, []);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!hasMore || isLoadingMore || !onLoadMore) return;

        const target = e.currentTarget;
        const scrollPercentage = (target.scrollTop + target.clientHeight) / target.scrollHeight;

        if (scrollPercentage > 0.8) {
            onLoadMore();
        }
    };

    const handleSelect = (value: string) => {
        if (mode === 'immediate') {
            onSelect(value);
        } else {
            setLocalSelectedValues(prev => {
                const newSet = new Set(prev);
                if (newSet.has(value)) {
                    newSet.delete(value);
                } else {
                    newSet.add(value);
                }
                return newSet;
            });
        }
    };

    const handleDismiss = () => {
        setSelectFilterOpened(false);
        onDismiss();
    };

    const handleApply = () => {
        if (mode === 'confirm' && onApply) {
            onApply(localSelectedValues);
            setSelectFilterOpened(false);
        }
    };

    const handleClear = () => {
        setLocalSelectedValues(new Set());
        if (onClear) {
            onClear();
        }
    };

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(groupId)) {
                newSet.delete(groupId);
            } else {
                newSet.add(groupId);
            }
            return newSet;
        });
    };

    const isGroupSelected = (group: FilterGroup) => {
        const displayValues = mode === 'confirm' ? localSelectedValues : selectedValues;

        const groupValues = group.items.map(item => item.value);
        const selectedCount = groupValues.filter(value => displayValues.has(value)).length;

        if (selectedCount === 0) return 'none';
        if (selectedCount === groupValues.length) return 'all';
        return 'partial';
    };

    const displayValues = mode === 'confirm' ? localSelectedValues : selectedValues;

    return (
        <ActionMenu open={selectFilterOpened} onOpenChange={setSelectFilterOpened}>
            <ActionMenu.Button className="w-8 h-8 right-[16px] absolute ml-auto invisible"></ActionMenu.Button>
            <ActionMenu.Overlay className="!w-[320px]">
                <ActionList>
                    <Stack justify="space-between" direction="horizontal">
                        <Stack direction="horizontal">
                            <Button
                                leadingVisual={isShowBack ? ChevronLeftIcon : undefined}
                                variant='invisible'
                                onClick={() => isShowBack && handleDismiss()}
                                disabled={!isShowBack}
                                className={!isShowBack ? 'pointer-events-none' : ''}>
                                <Text className='block mb-[1.5px] text-aloix-black'>{label}</Text>
                            </Button>
                        </Stack>
                    </Stack>
                    {onSearch && (
                        <div className='ml-4 mr-4 mb-2 mt-4'>
                            <TextInput
                                leadingVisual={SearchIcon}
                                className='w-full'
                                trailingAction={
                                    searchQuery.length > 0 ? (
                                        <TextInput.Action
                                            onClick={() => handleSearchChange('')}
                                            icon={XCircleFillIcon}
                                            aria-label="Очистить"
                                        />
                                    ) : undefined
                                }
                                value={searchQuery}
                                placeholder={searchPlaceholder}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                        </div>
                    )}
                    <div
                        className='ml-4 mr-4 max-h-[400px] overflow-y-auto'
                        onScroll={handleScroll}
                    >
                        <ActionList selectionVariant="multiple" role="menu" aria-label={label}>
                            {isLoading && items.length === 0 ? (
                                <ActionList.Item>Загрузка...</ActionList.Item>
                            ) : items.length === 0 ? (
                                <ActionList.Item>Ничего не найдено</ActionList.Item>
                            ) : isGrouped && groups ? (
                                <>
                                    {groups.map((group) => {
                                        const isExpanded = expandedGroups.has(group.id);
                                        const selectionState = isGroupSelected(group);

                                        return (
                                            <React.Fragment key={group.id}>
                                                <ActionList.Item
                                                    role="menuitemcheckbox"
                                                    selected={selectionState !== 'none'}
                                                    aria-checked={selectionState !== 'none'}
                                                    onSelect={(e) => {
                                                        e.preventDefault();
                                                        toggleGroup(group.id);
                                                    }}
                                                >
                                                    <div className="flex items-center justify-between w-full">
                                                        <div className="flex items-center gap-2 flex-1">
                                                            <span>{group.label}</span>
                                                        </div>
                                                    </div>
                                                </ActionList.Item>
                                                {isExpanded && (
                                                    group.items.map((item) => {
                                                        return (
                                                            <ActionList.Item
                                                                key={item.value}
                                                                role="menuitemcheckbox"
                                                                selected={displayValues.has(item.value)}
                                                                aria-checked={displayValues.has(item.value)}
                                                                onSelect={(e) => {
                                                                    e.preventDefault();
                                                                    handleSelect(item.value);
                                                                }}
                                                                style={{ paddingLeft: '20px' }}
                                                            >
                                                                {item.label}
                                                            </ActionList.Item>
                                                        );
                                                    })
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </>
                            ) : (
                                <>
                                    {items.map((item, index) => {
                                        const getItemValueString = (itemValue: any): string => {
                                            if (typeof itemValue === 'string') return itemValue;

                                            if (itemValue?.value && typeof itemValue.value === 'string') {
                                                return itemValue.value;
                                            }

                                            if (itemValue?.id && typeof itemValue.id === 'string') {
                                                return itemValue.id;
                                            }

                                            if (itemValue?.title && typeof itemValue.title === 'string') {
                                                return itemValue.title;
                                            }

                                            const result = String(itemValue);

                                            if (result === '[object Object]') {
                                                console.error('Cannot extract string value from:', itemValue);
                                                return itemValue?.id || itemValue?.value || JSON.stringify(itemValue);
                                            }

                                            return result;
                                        };
                                        const itemValueString = getItemValueString(item.value);
                                        const isSelected = displayValues.has(itemValueString) || displayValues.has(item.label);

                                        return (
                                            <ActionList.Item
                                                key={`${itemValueString}-${index}`}
                                                role="menuitemcheckbox"
                                                selected={isSelected}
                                                aria-checked={isSelected}
                                                onSelect={(e) => {
                                                    e.preventDefault();
                                                    handleSelect(itemValueString);
                                                }}
                                            >
                                                {item.label}
                                            </ActionList.Item>
                                        );
                                    })}
                                    {isLoadingMore && (
                                        <ActionList.Item>Загрузка...</ActionList.Item>
                                    )}
                                    {hasMore && !isLoadingMore && (
                                        <div className="p-2 text-center text-xs text-gray-500">
                                            Прокрутите для загрузки еще
                                        </div>
                                    )}
                                </>
                            )}
                        </ActionList>
                    </div>
                    {mode === 'confirm' && (
                        <Stack direction="horizontal" gap='normal' className='block mt-4 ml-[138px] mb-2'>
                            <Button
                                onClick={handleClear}
                                size='small'
                                variant="default">
                                Очистить
                            </Button>
                            <Button
                                onClick={handleApply}
                                size='small'
                                variant="primary">
                                Применить
                            </Button>
                        </Stack>
                    )}
                </ActionList>
            </ActionMenu.Overlay>
        </ActionMenu>
    );
};